import { Card, CardContent } from '@/components/ui/card';
import { MapPin, ArrowUpRight, ArrowDownRight } from 'lucide-react';

interface RealWageInsightProps {
  /** BEA Regional Price Parity index for the area (national = 100). */
  rppIndex?: number | null;
  nominalMean?: number | null;
  realMean?: number | null;
  locationName: string;
  className?: string;
}

const formatCurrency = (value?: number | null) =>
  value == null ? '-' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Math.round(value));

/**
 * Surfaces the cost-of-living ("real wage") differentiator: how far a salary
 * actually goes in a location once prices are accounted for. This is data
 * WageAtlas already loads (BEA RPP) but competitors rarely show.
 */
const RealWageInsight = ({ rppIndex, nominalMean, realMean, locationName, className = '' }: RealWageInsightProps) => {
  if (rppIndex == null || nominalMean == null || realMean == null) return null;

  const cheaper = rppIndex < 100;
  const pctDiff = Math.abs(Math.round((100 - rppIndex)));

  return (
    <Card className={`border-wage-secondary/30 bg-wage-secondary/5 ${className}`}>
      <CardContent>
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-wage-secondary/10">
            <MapPin className="h-5 w-5 text-wage-secondary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold mb-1 flex items-center gap-2">
              What your paycheck is really worth in {locationName}
              {cheaper ? (
                <ArrowUpRight className="h-4 w-4 text-green-600" />
              ) : (
                <ArrowDownRight className="h-4 w-4 text-amber-600" />
              )}
            </h3>
            <p className="text-sm text-muted-foreground mb-3">
              Prices in {locationName} run <strong>{pctDiff}% {cheaper ? 'below' : 'above'}</strong> the national
              average (RPP {rppIndex.toFixed(1)}). So a nominal salary of {formatCurrency(nominalMean)} has the buying
              power of about <strong>{formatCurrency(realMean)}</strong> in national terms.
            </p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="p-3 bg-background rounded-lg border">
                <div className="text-muted-foreground">Nominal (sticker) pay</div>
                <div className="text-lg font-bold">{formatCurrency(nominalMean)}</div>
              </div>
              <div className="p-3 bg-background rounded-lg border">
                <div className="text-muted-foreground">Real (purchasing power)</div>
                <div className="text-lg font-bold text-wage-secondary">{formatCurrency(realMean)}</div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default RealWageInsight;
